// Definición centralizada de roles y acciones

export type RoleKey =
  | 'admin_general'
  | 'admin_blog'
  | 'admin_comunidad'
  | 'admin_descuentos'
  | 'autor'
  | 'miembro'
  | 'miembro_estudiante'
  | 'miembro_academia'
  | 'miembro_maker'
  | 'moderador'
  | 'soporte'
  | 'invitado';

export type ActionKey =
  | 'user.manage'
  | 'user.view'
  | 'role.assign'
  | 'membership.manage'
  | 'membership.view'
  | 'discount.manage'
  | 'discount.apply'
  | 'blog.create'
  | 'blog.edit'
  | 'blog.delete'
  | 'blog.review'
  | 'blog.publish'
  | 'blog.view'
  // Acciones avanzadas de blog (artículos)
  | 'blog.article.create'
  | 'blog.article.update'
  | 'blog.article.publish'
  | 'blog.article.schedule'
  | 'blog.article.archive'
  | 'blog.article.delete'
  | 'blog.article.duplicate'
  | 'blog.article.view'
  // Taxonomía
  | 'blog.taxonomy.manage'
  | 'blog.taxonomy.view'
  // Biblioteca de medios
  | 'blog.media.manage'
  | 'blog.media.view'
  // Comentarios
  | 'blog.comments.moderate'
  | 'blog.comments.view'
  // Workflow configurable
  | 'blog.workflow.configure'
  // Versionado
  | 'blog.version.manage'
  | 'blog.version.view'
  // Redirecciones
  | 'blog.redirect.manage'
  | 'blog.redirect.view'
  // Analytics
  | 'blog.analytics.view'
  // Settings
  | 'blog.settings.manage'
  // Importación y exportación
  | 'blog.import'
  | 'blog.export'
  | 'community.manage'
  | 'community.moderate'
  | 'community.view'
  | 'community.group.join'
  | 'community.group.leave';

export type ResourceKey =
  | 'users'
  | 'memberships'
  | 'discounts'
  | 'blog'
  | 'community'
  | 'newsletter'
  | 'calculator';

// Matriz RBAC simple: acciones permitidas por rol
export const ROLE_PERMISSIONS: Record<RoleKey, ActionKey[]> = {
  admin_general: [
    'user.manage',
    'role.assign',
    'membership.manage',
    'discount.manage',
    'blog.create',
    'blog.edit',
    'blog.delete',
    'blog.review',
    'blog.publish',
    // Avanzado blog
    'blog.article.create',
    'blog.article.update',
    'blog.article.publish',
    'blog.article.schedule',
    'blog.article.archive',
    'blog.article.delete',
    'blog.article.duplicate',
    'blog.article.view',
    'blog.taxonomy.manage',
    'blog.taxonomy.view',
    'blog.media.manage',
    'blog.media.view',
    'blog.comments.moderate',
    'blog.comments.view',
    'blog.workflow.configure',
    'blog.version.manage',
    'blog.version.view',
    'blog.redirect.manage',
    'blog.redirect.view',
    'blog.analytics.view',
    'blog.settings.manage',
    'blog.import',
    'blog.export',
    'community.manage',
  ],
  admin_blog: [
    'blog.create', 'blog.edit', 'blog.delete', 'blog.review', 'blog.publish', 'blog.view',
    // Avanzado blog
    'blog.article.create',
    'blog.article.update',
    'blog.article.publish',
    'blog.article.schedule',
    'blog.article.archive',
    'blog.article.delete',
    'blog.article.duplicate',
    'blog.article.view',
    'blog.taxonomy.manage',
    'blog.taxonomy.view',
    'blog.media.manage',
    'blog.media.view',
    'blog.comments.moderate',
    'blog.comments.view',
    'blog.workflow.configure',
    'blog.version.manage',
    'blog.version.view',
    'blog.redirect.manage',
    'blog.redirect.view',
    'blog.analytics.view',
    'blog.settings.manage',
    'blog.import',
    'blog.export'
  ],
  admin_comunidad: ['community.manage', 'community.moderate', 'community.view'],
  admin_descuentos: ['discount.manage'],
  autor: [
    'blog.create', 'blog.view',
    // Avanzado limitado a propios
    'blog.article.create',
    'blog.article.update',
    'blog.article.view',
    'blog.media.view'
  ],
  miembro: ['membership.view', 'discount.apply', 'blog.view', 'community.view', 'community.group.join', 'community.group.leave'],
  miembro_estudiante: ['membership.view', 'discount.apply', 'blog.view', 'community.view', 'community.group.join', 'community.group.leave'],
  miembro_academia: ['membership.view', 'discount.apply', 'blog.view', 'community.view', 'community.group.join', 'community.group.leave'],
  miembro_maker: ['membership.view', 'discount.apply', 'blog.view', 'community.view', 'community.group.join', 'community.group.leave'],
  moderador: ['community.moderate', 'community.view', 'community.group.join', 'community.group.leave'],
  soporte: ['user.view'],
  invitado: ['blog.view', 'community.view'],
};

// Reglas ABAC base (evaluadas por util de permisos)
export const ABAC_RULES = {
  discounts: {
    // Ejemplos de elegibilidad
    estudiante: (attributes: any) => attributes?.membership_tier === 'estudiante' && attributes?.student_verified === true,
    academia: (attributes: any) => attributes?.membership_tier === 'academia',
    maker: (attributes: any) => attributes?.membership_tier === 'maker',
  },
};